// absolute imports
import { useReactiveVar } from '@apollo/client';
import { IconButton, MenuItem, MenuList } from '@mui/material';
import { useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

// icon imports
import {
  ContactSupportOutlined as ContactUsIcon,
  HelpOutline as HelpIcon,
  VisibilityOffOutlined as HideIcon,
  SchoolOutlined as KnowledgeBaseIcon,
  VisibilityOutlined as ShowIcon,
} from '@mui/icons-material';

// relative imports
import { showHelpWidgetVar } from '../../../data/apollo/cache/reactiveVars';
import ExtendedPopper from '../custom-components/ExtendedPopper';
import { openZendeskWidget } from './zendesk-operations';

const useStyles = makeStyles()((theme) => ({
  supportButton: {
    color: theme.whiteLabel.Navigation.color || theme.palette.common.white,
    marginRight: theme.spacing(2),
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.2)',
    },
  },
  helpIcon: {
    fontSize: theme.typography.pxToRem(24),
  },
  menuList: {
    padding: theme.spacing(0),
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: theme.typography.pxToRem(14),
    '&:hover': {
      color: '#000',
      textDecoration: 'none',
    },
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  menuIcon: {
    marginRight: theme.spacing(1),
  },
}));

function getKbLinkFromContext() {
  return 'https://support.ledgible.io/hc/en-us';
}

const HelpMenu = () => {
  const { classes } = useStyles();
  const showHelpWidget = useReactiveVar(showHelpWidgetVar);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const kbLink = getKbLinkFromContext();

  const handleClose = (event) => {
    if (
      anchorRef.current &&
      event &&
      anchorRef.current.contains(event.target)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleToggleMenu = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const toggleHelpWidgetVisibility = () => {
    showHelpWidgetVar(!showHelpWidget);
  };

  return (
    <ExtendedPopper
      content={
        <MenuList disableListWrap className={classes.menuList}>
          <MenuItem
            component="a"
            onClick={handleClose}
            href={kbLink}
            className={classes.menuItem}
            target="_blank"
            id="support-menu-item-help"
            data-cy="support-menu-item-product-guide-link"
            divider
          >
            <KnowledgeBaseIcon className={classes.menuIcon} fontSize="small" />
            Product Guide
          </MenuItem>
          <MenuItem
            onClick={() => {
              openZendeskWidget();
              handleClose();
            }}
            className={classes.menuItem}
            id="support-menu-item-contact-us"
            divider
          >
            <ContactUsIcon className={classes.menuIcon} fontSize="small" />
            Contact Support
          </MenuItem>
          <MenuItem
            onClick={toggleHelpWidgetVisibility}
            className={classes.menuItem}
            id="support-menu-item-contact-us"
          >
            {showHelpWidget ? (
              <HideIcon className={classes.menuIcon} fontSize="small" />
            ) : (
              <ShowIcon className={classes.menuIcon} fontSize="small" />
            )}
            {showHelpWidget ? 'Hide Help Widget' : 'Show Help Widget'}
          </MenuItem>
        </MenuList>
      }
      arrow
      open={open}
      placement="bottom-end"
      clickAwayHandler={handleClose}
      padContent={false}
    >
      <IconButton
        aria-label="toggle help menu"
        onClick={handleToggleMenu}
        className={classes.supportButton}
        size="small"
        aria-controls="menu-list-grow"
        aria-haspopup="true"
        ref={anchorRef}
        id="top-nav-help-menu-button"
        data-cy="top-nav-help-menu-button"
      >
        <HelpIcon className={classes.helpIcon} />
      </IconButton>
    </ExtendedPopper>
  );
};

export default HelpMenu;
